import React, { useState, useEffect, useRef, type JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  RiArrowLeftSLine,
  RiHome4Line,
  RiMenuFill,
  RiCloseLine,
  RiNewspaperFill,
} from "@remixicon/react";
import { getMenus, type MenuItem } from "@/components/navigation/routes-menus"; // Asegúrate que la ruta sea correcta
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useBackStore } from "@/stores/backStore";
import { api } from "@/services/apiService";

// Definición local de glass-effect para asegurar la transparencia para el backdrop-filter
// Idealmente, esta sería una clase global si la usas en múltiples componentes.
const FooterNavStyles = () => (
  <style>{`
    /* Footer y popup con fondos sólidos (sin transparencias) */
    .footer-glass-effect {
      background: #ffffff; /* light */
      border: 1px solid #e5e7eb; /* slate-200/300 */
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    .popup-menu-glass-effect {
      background: #fefefe; /* blanco crema */
      border: 1px solid #475569; /* slate-600 */
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    }

    /* Legacy-safe positioning */
    .footer-nav-container {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 50;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      padding: 8px;
      pointer-events: none;
    }

    .footer-nav-inner {
      pointer-events: auto;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-pack: distribute;
      -ms-flex-pack: distribute;
      justify-content: space-around;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      padding: 6px 12px;
      border-radius: 16px;
      width: 100%;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    /* Responsive adjustments for desktop */
    @media (min-width: 768px) {
      .footer-nav-inner {
        width: auto;
        min-width: 200px;
        padding: 6px 20px;
        border-radius: 50px;
      }
    }

    .menu-popup-container {
      position: fixed;
      bottom: 80px;
      left: 50%;
      /* Modern centering */
      transform: translateX(-50%);
      width: 94%;
      max-width: 28rem;
      max-height: 60vh;
      z-index: 60;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
      -ms-flex-direction: column;
      flex-direction: column;
      padding: 12px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    }

    /* Legacy fallback: use simple positioning without transform */
    .legacy .menu-popup-container {
      left: 3%;
      transform: none;
      margin-left: 0;
      width: 94%;
      max-width: none;
    }

    @media (min-width: 768px) {
      .menu-popup-container {
        bottom: 90px;
        transform: translateX(-50%);
        width: 320px;
        max-height: 65vh;
      }
      
      .legacy .menu-popup-container {
        left: 50%;
        transform: none;
        margin-left: -160px;
        width: 320px;
      }
    }

    /* Dark theme support */
    @media (prefers-color-scheme: dark) {
      .footer-glass-effect { background: #1f2937; border: 1px solid #334155; box-shadow: 0 3px 10px rgba(0,0,0,0.4); }
      .popup-menu-glass-effect { background: #1f2937; border: 1px solid #334155; box-shadow: 0 4px 16px rgba(0,0,0,0.45); }
    }

    /* Scrollbar (discreto y sólido) */
    .popup-menu-scrollbar::-webkit-scrollbar { width: 6px; }
    .popup-menu-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .popup-menu-scrollbar::-webkit-scrollbar-thumb { background: #64748b; border-radius: 3px; }

    /* Alto contraste */
    @media (prefers-contrast: high) {
      .footer-glass-effect { background: #ffffff !important; border: 2px solid #000000 !important; box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important; }
      .popup-menu-glass-effect { background: #ffffff !important; border: 2px solid #000000 !important; box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important; }
      .text-white { color: #000000 !important; }
      .text-gray-200 { color: #000000 !important; }
      .text-blue-300 { color: #0078d4 !important; }
    }

    /* Legacy fallbacks */
    .legacy .footer-glass-effect { background: #f8f9fa !important; border: 1px solid #dee2e6 !important; }
    .legacy .popup-menu-glass-effect { 
      background: #ffffff !important; 
      border: 1px solid #dee2e6 !important; 
      color: #212529 !important;
    }
    .legacy .popup-menu-glass-effect .text-white { color: #212529 !important; }
    .legacy .popup-menu-glass-effect .text-gray-200 { color: #495057 !important; }
    .legacy .popup-menu-glass-effect .text-blue-300 { color: #0d6efd !important; }
    .legacy .popup-menu-glass-effect .text-gray-300 { color: #6c757d !important; }
    .legacy .popup-menu-glass-effect .text-gray-700 { color: #212529 !important; }
    .legacy .popup-menu-glass-effect .text-gray-800 { color: #212529 !important; }
    .legacy .popup-menu-glass-effect .text-gray-900 { color: #212529 !important; }
    .legacy .popup-menu-glass-effect .hover\\:bg-slate-200:hover { background-color: #e9ecef !important; }
    .legacy .popup-menu-glass-effect .hover\\:bg-slate-700:hover { background-color: #e9ecef !important; }
  `}</style>
);

function findMenuByHref(menus: MenuItem[], href: string): MenuItem | null {
  for (const menu of menus) {
    if (menu.href === href) return menu;
    if (menu.children) {
      const found = findMenuByHref(menu.children, href);
      if (found) return found;
    }
  }
  return null;
}

export function FooterNav() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [allMenus, setAllMenus] = useState<MenuItem[]>([]);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPopupRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [shouldHideFooter, setShouldHideFooter] = useState(false);
  const [isLegacy, setIsLegacy] = useState(false);

  // Obtener la ruta de regreso del store
  const backRoute = useBackStore((state) => state.backRoute);
  const clearBack = useBackStore((state) => state.clearBack);

  // Estado para documentación en pantalla completa
  const [showDocs, setShowDocs] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState("");
  const [doc, setDoc] = useState<{
    titulo: string;
    contenido_html: string;
  } | null>(null);
  const [hasDoc, setHasDoc] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setAllMenus(getMenus()); // Carga los menús cuando el componente se monta
  }, []);

  // Detect legacy browser features (same logic as Home)
  useEffect(() => {
    try {
      const supports = (prop: string, value?: string) => {
        try {
          const css = CSS as unknown as {
            supports?: (conditionText: string) => boolean;
          };
          if (typeof css?.supports === "function") {
            return css.supports(`${prop}: ${value ?? ""}`);
          }
        } catch {
          /* ignore */
        }
        return false;
      };
      const hasTransform = supports("transform", "translateX(-50%)");
      const hasFlexbox = supports("display", "flex");
      const ua = navigator.userAgent || "";
      const veryOldUA = /MSIE\s|Trident\/|Edge\/(12|13|14)/.test(ua);
      setIsLegacy(!(hasTransform && hasFlexbox) || veryOldUA);
    } catch {
      setIsLegacy(true);
    }
  }, []);

  // Effect to monitor body class changes for hiding footer
  useEffect(() => {
    const checkBodyClass = () => {
      setShouldHideFooter(document.body.classList.contains("hide-footer-nav"));
    };

    // Check initially
    checkBodyClass();

    // Create a MutationObserver to watch for class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          checkBodyClass();
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Cleanup
    return () => observer.disconnect();
  }, []);

  //verifica si tiene o no doc el menu actual
  useEffect(() => {
    const currentMenu = findMenuByHref(allMenus, location.pathname);
    setHasDoc(!!currentMenu?.id_documentacion);
  }, [allMenus, location.pathname]);

  const handleBack = () => {
    if (backRoute) {
      // Si hay una ruta específica configurada, navegar ahí y limpiar el store
      navigate(backRoute);
      clearBack();
    } else {
      // Comportamiento por defecto: ir atrás en el historial
      navigate(-1);
    }
  };

  const handleHome = () => navigate("/home");
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuNavigation = (href: string | null) => {
    setLoading(true);
    if (href) {
      // Verifica si el href es una URL externa (comienza con http:// o https://)
      if (href.startsWith("http://") || href.startsWith("https://")) {
        window.location.href = href; // Redirige a la URL externa
        setTimeout(() => {
          setLoading(false);
        }, 500);
      } else {
        navigate(href); // Navegación interna con React Router
        setLoading(false);
      }
      setIsMenuOpen(false); // Cierra el menú después de la navegación
    }
  };

  // Cargar y mostrar documentación en pantalla completa
  const cargarDoc = async (id: number) => {
    setDocLoading(true);
    setDocError("");
    setDoc(null);
    try {
      const res = await api.get(`/api/config/get-documentacion?id=${id}`);
      if (res.data && Array.isArray(res.data) && res.data[0]) {
        const item = res.data[0] as {
          titulo?: string;
          contenido_html?: string;
        };
        const rawHtml = item.contenido_html || "<p>Sin contenido.</p>";
        // Decodificar entidades HTML si vienen escapadas
        const decodedHtml = /&lt;\/?[a-zA-Z]/.test(rawHtml)
          ? (() => {
              const textarea = document.createElement("textarea");
              textarea.innerHTML = rawHtml;
              return textarea.value;
            })()
          : rawHtml;
        setDoc({
          titulo: item.titulo || "Documentación",
          contenido_html: decodedHtml,
        });
      } else {
        setDoc({
          titulo: "Documentación",
          contenido_html: "<p>Sin contenido.</p>",
        });
      }
    } catch (err: unknown) {
      setDocError(
        err instanceof Error ? err.message : "Error al cargar documentación"
      );
    } finally {
      setDocLoading(false);
    }
  };

  const openDocs = async () => {
    const currentMenu = findMenuByHref(allMenus, location.pathname);
    const id = currentMenu?.id_documentacion ?? null;
    if (!id) return;
    setShowDocs(true);
    void cargarDoc(id);
  };

  const closeDocs = () => setShowDocs(false);

  // Cerrar menú si se hace clic fuera de él
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isMenuOpen &&
        menuPopupRef.current &&
        !menuPopupRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const renderPopupMenuItem = (item: MenuItem, level = 0): JSX.Element => (
    <React.Fragment key={`${item.id}-${level}`}>
      <button
        onClick={() => handleMenuNavigation(item.href)}
        className={`w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center group
                    ${level > 0 ? "pl-" + (5 + level * 2) : "pl-3"} 
                    ${
                      !item.href && !item.children?.length
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:text-blue-700 dark:hover:text-blue-200"
                    }`}
        disabled={!item.href && !item.children?.length}
        title={item.label}
      >
        {item.icon && (
          <item.icon
            className={`w-5 h-5 mr-3.5 shrink-0 ${
              level === 0
                ? "text-blue-300"
                : "text-gray-300 group-hover:text-blue-200"
            }`}
          />
        )}
        <span
          className={`text-sm truncate ${
            level === 0 && !item.children
              ? "font-medium text-gray-800 dark:text-gray-100 group-hover:text-blue-900 dark:group-hover:text-white"
              : level === 0 && item.children
              ? "font-semibold text-gray-900 dark:text-gray-50"
              : "text-gray-700 dark:text-gray-200 group-hover:text-blue-900 dark:group-hover:text-white"
          }`}
        >
          {item.label}
        </span>
        {item.children && item.children.length > 0 && !item.href && (
          <span className="ml-auto text-xs text-gray-400 group-hover:text-blue-300">
            {/* Podrías usar un ícono de flecha aquí si lo prefieres */}
          </span>
        )}
      </button>
      {item.children && item.children.length > 0 && (
        <div className={`ml-4 border-l-2 border-blue-600 rounded-bl-sm`}>
          {item.children.map((child) => renderPopupMenuItem(child, level + 1))}
        </div>
      )}
    </React.Fragment>
  );

  return (
    <>
      {showDocs && (
        <div className="fixed inset-0 z-[70] bg-base-100 overflow-y-auto p-4">
          {/* Scoped styles to keep content readable over a solid white box */}
          <style>{`
            .doc-overlay .doc-box { background:#ffffff; border:1px solid #e5e7eb; border-radius:12px; }
            .doc-overlay .doc-content { color:#1f2937; }
            .doc-overlay .doc-content img { max-width:100%; height:auto; }
          `}</style>
          <div className="max-w-2xl mx-auto doc-overlay">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {doc?.titulo || "Documentación"}
              </h2>
              <button
                onClick={closeDocs}
                className="btn btn-xs btn-outline"
                aria-label="Cerrar documentación"
              >
                Cerrar
              </button>
            </div>
            {docLoading && (
              <div className="text-sm opacity-70">Cargando...</div>
            )}
            {docError && (
              <div className="alert alert-error p-2 text-xs mb-3">
                {docError}
              </div>
            )}
            {!docLoading && !docError && (
              <div className="doc-box shadow p-4 md:p-6">
                <article
                  className="prose max-w-none text-sm doc-content"
                  dangerouslySetInnerHTML={{
                    __html: doc?.contenido_html || "<p>Sin contenido.</p>",
                  }}
                />
              </div>
            )}
            <div className="mt-6 flex justify-center">
              <button
                onClick={closeDocs}
                className="btn btn-primary btn-sm"
                aria-label="Cerrar"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      )}
      {loading && <LoadingSpinner />}
      <FooterNavStyles />
      {!shouldHideFooter && (
        <div className={isLegacy ? "legacy" : ""}>
          {isMenuOpen && (
            <div
              ref={menuPopupRef}
              className="menu-popup-container popup-menu-glass-effect"
            >
              <div className="flex justify-between items-center mb-2 px-1">
                <h3 className="text-md font-semibold text-white">
                  Menú Principal
                </h3>
                <button
                  onClick={toggleMenu}
                  className="p-1.5 rounded-full hover:bg-slate-600 dark:hover:bg-slate-500 transition-colors"
                  aria-label="Cerrar menú"
                >
                  <RiCloseLine className="w-5 h-5 text-gray-200" />
                </button>
              </div>
              <div className="overflow-y-auto flex-grow space-y-0.5 popup-menu-scrollbar pr-1">
                {allMenus.map((item) => renderPopupMenuItem(item, 0))}
              </div>
            </div>
          )}

          <div className="footer-nav-container">
            <div className="footer-nav-inner footer-glass-effect">
              <button
                onClick={handleBack}
                className={`p-2.5 md:p-3 rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 relative ${
                  backRoute
                    ? "text-blue-700 bg-blue-100 dark:text-blue-200 dark:bg-blue-900"
                    : "text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300"
                }`}
                aria-label="Atrás"
                title={"Ir atrás"}
              >
                <RiArrowLeftSLine size={28} className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              <button
                onClick={handleHome}
                className="p-2.5 md:p-3 text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Inicio"
              >
                <RiHome4Line size={28} className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              <button
                ref={menuButtonRef}
                onClick={toggleMenu}
                className={`p-2.5 md:p-3 rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isMenuOpen
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                    : "text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300"
                }`}
                aria-label="Menú"
                aria-expanded={isMenuOpen}
              >
                <RiMenuFill size={28} className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              {hasDoc && (
                <button
                  onClick={() => {
                    if (!showDocs) void openDocs();
                  }}
                  className={`p-2.5 md:p-3 rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    isMenuOpen
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                      : "text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300"
                  }`}
                  aria-label="Ver documentación"
                  disabled={showDocs}
                >
                  <RiNewspaperFill
                    size={28}
                    className="w-6 h-6 md:w-7 md:h-7"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
